import { t } from "@lingui/macro";
import { Check, Crown, X } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";

type CompetitorData = {
  name: string;
  highlight?: boolean;
  features: Record<string, boolean | string>;
};

const featureRows = [
  "AI Resume Writing",
  "ATS Score (0-100)",
  "Job Description Matching",
  "Auto-Tailoring",
  "AI Cover Letters",
  "AI Chat Assistant",
  "DOCX Export",
  "LinkedIn Import",
  "Starting Price/mo",
];

const competitors: CompetitorData[] = [
  {
    name: "CareerBldr",
    highlight: true,
    features: {
      "AI Resume Writing": true,
      "ATS Score (0-100)": true,
      "Job Description Matching": true,
      "Auto-Tailoring": true,
      "AI Cover Letters": true,
      "AI Chat Assistant": true,
      "DOCX Export": true,
      "LinkedIn Import": true,
      "Starting Price/mo": "$9",
    },
  },
  {
    name: "Zety",
    features: {
      "AI Resume Writing": false,
      "ATS Score (0-100)": false,
      "Job Description Matching": false,
      "Auto-Tailoring": false,
      "AI Cover Letters": false,
      "AI Chat Assistant": false,
      "DOCX Export": true,
      "LinkedIn Import": false,
      "Starting Price/mo": "$26",
    },
  },
  {
    name: "Resume.io",
    features: {
      "AI Resume Writing": false,
      "ATS Score (0-100)": false,
      "Job Description Matching": false,
      "Auto-Tailoring": false,
      "AI Cover Letters": false,
      "AI Chat Assistant": false,
      "DOCX Export": false,
      "LinkedIn Import": false,
      "Starting Price/mo": "$25",
    },
  },
  {
    name: "Kickresume",
    features: {
      "AI Resume Writing": "Basic",
      "ATS Score (0-100)": "Basic",
      "Job Description Matching": false,
      "Auto-Tailoring": false,
      "AI Cover Letters": "Basic",
      "AI Chat Assistant": false,
      "DOCX Export": true,
      "LinkedIn Import": true,
      "Starting Price/mo": "$24",
    },
  },
  {
    name: "Teal",
    features: {
      "AI Resume Writing": "Limited",
      "ATS Score (0-100)": false,
      "Job Description Matching": true,
      "Auto-Tailoring": "Limited",
      "AI Cover Letters": true,
      "AI Chat Assistant": false,
      "DOCX Export": false,
      "LinkedIn Import": false,
      "Starting Price/mo": "$52",
    },
  },
];

const CellValue = ({ value, highlight }: { value: boolean | string; highlight?: boolean }) => {
  if (value === true) return <Check weight="bold" className="mx-auto h-5 w-5 text-green-500" />;
  if (value === false) return <X weight="bold" className="mx-auto h-5 w-5 text-foreground/20" />;
  if (highlight && value === "$9") {
    return <span className="text-xs font-semibold text-primary">{value}</span>;
  }
  return <span className="text-xs text-foreground/50">{value}</span>;
};

export const ComparisonSection = () => (
  <section id="comparison" className="relative py-24 sm:py-32">
    <div className="container">
      <motion.div
        className="mx-auto max-w-2xl text-center"
        viewport={{ once: true }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        <p className="text-sm font-semibold uppercase tracking-wider text-primary">
          {t`Compare`}
        </p>
        <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
          {t`Best AI Resume Builder Compared`}
        </h2>
        <p className="mt-4 text-base leading-relaxed text-foreground/60">
          {t`The most AI features at a fair price. See why professionals switch to CareerBldr.`}
        </p>
      </motion.div>

      <motion.div
        className="mx-auto mt-12 max-w-5xl overflow-x-auto"
        viewport={{ once: true }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}
      >
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-border/50">
              <th className="pb-4 pr-4 text-left font-medium text-foreground/50">
                {t`Feature`}
              </th>
              {competitors.map((c) => (
                <th
                  key={c.name}
                  className={cn(
                    "pb-4 text-center font-semibold",
                    c.highlight && "text-primary",
                  )}
                >
                  <div className="flex flex-col items-center gap-1">
                    {c.highlight && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold text-primary">
                        <Crown size={10} weight="fill" />
                        Recommended
                      </span>
                    )}
                    {c.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {featureRows.map((feature) => (
              <tr key={feature} className="border-b border-border/30">
                <td className="py-3.5 pr-4 text-foreground/70">{feature}</td>
                {competitors.map((c) => (
                  <td
                    key={c.name}
                    className={cn(
                      "py-3.5 text-center",
                      c.highlight && "bg-primary/5",
                    )}
                  >
                    <CellValue value={c.features[feature]} highlight={c.highlight} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>
    </div>
  </section>
);
